import { DSNotification } from 'components/DSNotification';
import { useLocationContext } from 'contexts/LocationContext';
import { useTunnusContext } from 'contexts/TunnusContext';
import { useTranslation } from 'hooks/useTranslation';
import { MediaType, Region } from 'services/areena-api/types';

type Props = {
  programMediaType: MediaType | undefined;
  programRegion: Region | undefined;
};

type State =
  | 'no-restriction'
  | 'not-logged-in'
  | 'not-in-portability-region'
  | 'no-residence-in-finland';

export const GeoRestrictionNotification: React.FC<Props> = ({
  programMediaType,
  programRegion,
}) => {
  const isAudioProgram = programMediaType === 'audio';
  const state = useGeoRestrictionState(programRegion);
  const t = useTranslation();

  switch (state) {
    case 'not-logged-in':
      return (
        <DSNotification
          title={t('common_longer_login')}
          subtitle={t('toast_notification_euloginrequest')}
          actionControl={{
            type: 'activator',
            title: t('logIn'),
            functionality: { id: 'login' },
          }}
          state="primary"
          type="info"
        />
      );

    case 'no-residence-in-finland':
      return (
        <DSNotification
          title={t('common_geolocation_confirmresidence')}
          subtitle={
            isAudioProgram
              ? t('toast_notification_eu_verification_content_listen')
              : t('common_geolocation_onlyeu')
          }
          actionControl={{
            type: 'activator',
            title: t('toast_notification_eu_verification_button'),
            functionality: { id: 'tunnus.begin-identification' },
          }}
          state="primary"
          type="info"
        />
      );

    case 'not-in-portability-region':
      return (
        <DSNotification
          title={
            isAudioProgram
              ? t('toast_notification_eu_verification_content_listen')
              : t('common_geolocation_onlyeu')
          }
          state="primary"
          type="info"
        />
      );

    default:
      return null;
  }
};

export function useGeoRestrictionState(
  programRegion: Region | undefined
): State {
  const {
    country_code: currentCountry,
    is_portability_region: userIsInPortabilityRegion,
  } = useLocationContext() || {};
  const { isAuthenticated: userIsLoggedIn, userInformation } =
    useTunnusContext();

  if (!programRegion || programRegion === 'world') return 'no-restriction';
  if (!currentCountry || currentCountry === 'FI') return 'no-restriction';
  if (userIsInPortabilityRegion === false) return 'not-in-portability-region';
  if (!userIsLoggedIn) return 'not-logged-in';
  if (userInformation && !userInformation.residenceInFinland)
    return 'no-residence-in-finland';
  return 'no-restriction';
}
